/**
 * 门户路由滚动策略：Tab 切换回内容区顶部，其余场景按需保留或重置
 */
import { nextTick } from 'vue'
import {
  getMenuNodePath,
  isPathUnderMenu,
  isPortalMenuEnter,
  normalizeContentPath,
  normalizeMenuPath
} from '@/components/portal/common/utils/menuPath'
import {
  resolveEffectiveTemplateId,
  resolvePageRootMenu
} from '@/components/portal/common/utils/portalRouteResolver'
import { getTemplateEntry } from '@/components/portal/common/utils/menuTemplateRegistry'
import {
  isProductCatalogPath,
  isProductCategoryInPageSwitch
} from '@/components/modules/products/utils/productHelpers'
import { extractNewsDetailSlug, isNewsListPath } from '@/components/modules/news/utils/newsPages'
import { getHeaderOffset } from '@/components/shared/utils/viewportShell'

const PORTAL_TAB_CONTENT_ANCHORS = [
  '.news-panel__nav',
  '.about-detail__nav',
  '.products-body__nav-sticky',
  '.portal-back-bar'
]

/** 路径是否与某个 CMS 菜单节点 path 完全一致 */
export function findExactMenuPathMatch(path, menus = []) {
  const target = normalizeContentPath(path)
  if (!target || target === '/') return null

  let matched = null

  const walk = (nodes = []) => {
    for (const node of nodes) {
      const nodePath = getMenuNodePath(node)
      if (nodePath && nodePath === target) {
        matched = nodePath
      }
      if (node?.children?.length) walk(node.children)
    }
  }

  walk(menus)
  return matched
}

/** 在指定一级栏目子树中查找与 path 完全一致的菜单节点 */
export function findExactMenuPathMatchUnderRoot(path, rootMenu) {
  if (!rootMenu) return null

  const target = normalizeContentPath(path)
  if (!target) return null

  const rootPath = getMenuNodePath(rootMenu)
  if (rootPath && rootPath === target) return rootPath

  let matched = null

  const walk = (nodes = []) => {
    for (const node of nodes) {
      const nodePath = getMenuNodePath(node)
      if (nodePath && nodePath === target) {
        matched = nodePath
      }
      if (node?.children?.length) walk(node.children)
    }
  }

  walk(rootMenu.children || [])
  return matched
}

function getRelativeSegments(path, rootPath) {
  const current = normalizeContentPath(path)
  const base = normalizeMenuPath(rootPath)
  if (!base) return null
  if (current === base) return []
  if (!current.startsWith(`${base}/`)) return null
  return current.slice(base.length + 1).split('/').filter(Boolean)
}

function isSamePortalRoot(toRoot, fromRoot) {
  return Boolean(
    toRoot
    && fromRoot
    && String(toRoot.id) === String(fromRoot.id)
  )
}

function getRootTemplateKey(rootMenu, menus = [], locale) {
  if (!rootMenu) return ''
  const templateId = resolveEffectiveTemplateId(rootMenu, menus, locale)
  return getTemplateEntry(templateId).key || ''
}

/** 标签式列表：顶部分类 Tab 切换（排除列表 ↔ 详情） */
function isFocusCarouselTabSwitch(toPath, fromPath, toRoot, fromRoot, locale) {
  if (!isSamePortalRoot(toRoot, fromRoot)) return false
  if (getRootTemplateKey(toRoot, [], locale) !== 'FOCUS_CAROUSEL_LIST') return false
  return isNewsListPath(toPath, [], toRoot) && isNewsListPath(fromPath, [], fromRoot)
}

/** 标签式内容详情：保留横幅，不滚出视口 */
function isFocusCarouselDetailPath(path, rootMenu, locale) {
  if (getRootTemplateKey(rootMenu, [], locale) !== 'FOCUS_CAROUSEL_LIST') return false
  if (isNewsListPath(path, [], rootMenu)) return false
  return Boolean(extractNewsDetailSlug(path, [], rootMenu))
}

/** 菜单式概况：二/三级 Tab 切换（排除根网格 ↔ 子页） */
function isIntroCardGridTabSwitch(toPath, fromPath, toRoot, fromRoot, locale) {
  if (!isSamePortalRoot(toRoot, fromRoot)) return false
  if (getRootTemplateKey(toRoot, [], locale) !== 'INTRO_CARD_GRID') return false

  const rootPath = getMenuNodePath(toRoot)
  if (!isPathUnderMenu(toPath, rootPath) || !isPathUnderMenu(fromPath, rootPath)) return false

  const toSegs = getRelativeSegments(toPath, rootPath)
  const fromSegs = getRelativeSegments(fromPath, rootPath)
  if (!toSegs?.length || !fromSegs?.length) return false

  const toMatch = findExactMenuPathMatchUnderRoot(toPath, toRoot)
  const fromMatch = findExactMenuPathMatchUnderRoot(fromPath, fromRoot)
  if (toMatch && fromMatch) return true

  return toSegs.length === fromSegs.length && toSegs.length <= 2
}

/** 同栏目下任意菜单节点 path 之间切换 */
function isExactMenuTabSwitch(toPath, fromPath, toRoot, fromRoot, menus = [], locale) {
  if (!isSamePortalRoot(toRoot, fromRoot)) return false
  // 卡片式方案：列表 / 分类 / 详情是整页切换，不按 Tab 保留滚动
  if (getRootTemplateKey(toRoot, menus, locale) === 'TAB_PANEL_SWITCH') return false

  const rootPath = getMenuNodePath(toRoot)
  if (!isPathUnderMenu(toPath, rootPath) || !isPathUnderMenu(fromPath, rootPath)) return false

  const toExact = findExactMenuPathMatchUnderRoot(toPath, toRoot)
    || findExactMenuPathMatch(toPath, menus)
  const fromExact = findExactMenuPathMatchUnderRoot(fromPath, fromRoot)
    || findExactMenuPathMatch(fromPath, menus)

  return Boolean(toExact && fromExact)
}

/** 门户页内二/三级 Tab 切换（应滚到 Hero 下方内容区顶部） */
export function isPortalTabSwitch(to, from, menus = [], locale) {
  const toPath = normalizeContentPath(to?.path || '')
  const fromPath = normalizeContentPath(from?.path || '')

  if (!from?.matched?.length) return false
  if (normalizeMenuPath(toPath) === normalizeMenuPath(fromPath)) return false

  const toRoot = resolvePageRootMenu(toPath, menus)
  const fromRoot = resolvePageRootMenu(fromPath, menus)

  if (isFocusCarouselTabSwitch(toPath, fromPath, toRoot, fromRoot, locale)) return true
  if (isIntroCardGridTabSwitch(toPath, fromPath, toRoot, fromRoot, locale)) return true
  if (isExactMenuTabSwitch(toPath, fromPath, toRoot, fromRoot, menus, locale)) return true

  if (isProductCategoryInPageSwitch(toPath, fromPath)) {
    const productRoot = resolvePageRootMenu(toPath, menus)
    if (getRootTemplateKey(productRoot, menus, locale) !== 'CATEGORY_PORTAL_CARDS') return true
  }

  return false
}

/** 是否应在路由切换后保留当前滚动位置 */
export function shouldPreserveScrollOnRouteChange(to, from, menus = []) {
  const toPath = normalizeContentPath(to?.path || '')
  const fromPath = normalizeContentPath(from?.path || '')

  if (!from?.matched?.length) return false
  if (normalizeMenuPath(toPath) === normalizeMenuPath(fromPath)) return true
  if (isProductCatalogPath(toPath) && isProductCatalogPath(fromPath)) return true

  return false
}

/** 内容跳转藏横幅；顶栏菜单进入、标签式详情不走这里，交给置顶 */
export function shouldResetPortalPageScroll(to, from, menus = [], locale) {
  const toPath = normalizeContentPath(to?.path || '')
  const fromPath = normalizeContentPath(from?.path || '')

  if (!from?.matched?.length) return false
  if (normalizeMenuPath(toPath) === normalizeMenuPath(fromPath)) return false
  if (isPortalMenuEnter(to)) return false
  const toRoot = resolvePageRootMenu(toPath, menus)
  if (!toRoot) return false
  if (isPortalTabSwitch(to, from, menus, locale)) return false
  if (shouldPreserveScrollOnRouteChange(to, from, menus)) return false
  if (isFocusCarouselDetailPath(toPath, toRoot, locale)) return false

  return true
}

function alignPortalBelowHeroOnce() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false

  const hero = document.querySelector('.page-hero')
  if (!hero) return false

  const y = hero.getBoundingClientRect().bottom + window.scrollY
  const target = Math.max(0, y - getHeaderOffset())
  window.scrollTo({ top: target, left: 0, behavior: 'auto' })
  return true
}

function alignPortalContentAnchorOnce() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false

  for (const selector of PORTAL_TAB_CONTENT_ANCHORS) {
    const anchor = document.querySelector(selector)
    if (!anchor) continue

    const y = anchor.getBoundingClientRect().top + window.scrollY
    const target = Math.max(0, y - getHeaderOffset())
    window.scrollTo({ top: target, left: 0, behavior: 'auto' })
    return true
  }

  return false
}

/** Tab 切换：滚到 Hero 下方内容区顶部；无 Hero 时对齐 Tab/返回条 */
export function scrollPortalTabContentTop() {
  if (alignPortalBelowHeroOnce()) return true
  return alignPortalContentAnchorOnce()
}

/** 延迟重试，覆盖 v-csp-html 异步写入后的布局变化 */
export function scheduleScrollPortalTabContentTop() {
  const run = () => scrollPortalTabContentTop()

  run()
  nextTick(() => {
    run()
    requestAnimationFrame(() => {
      run()
      requestAnimationFrame(run)
    })
    window.setTimeout(run, 160)
  })
}

/** 将横幅刚好滚出视口，顶栏下直接是返回条；找不到横幅时不滚到 0 */
export function scrollPortalBelowHero() {
  if (alignPortalBelowHeroOnce()) return true

  requestAnimationFrame(() => {
    if (alignPortalBelowHeroOnce()) return
    requestAnimationFrame(alignPortalBelowHeroOnce)
  })
  return false
}
